import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { UseProctoringReturn } from 'proctoring-sdk/index'
import { readProctoringTimerRemaining } from 'Utils/proctoringTimer.utils'
import { ProctoringCountdownTimer } from './ProctoringCountdownTimer'
import { ProctoringRecordingBadge } from './ProctoringRecordingBadge'
import styles from './ProctoringTopChrome.module.less'

export const PROCTORING_HEADER_SLOT_ID = 'proctoring-header-slot'

export const ProctoringTopChrome = ({
    proctoring,
    durationSeconds,
    referenceId,
    onDurationEnd,
    profile,
}: {
    proctoring: UseProctoringReturn | null
    durationSeconds: number
    referenceId?: string
    onDurationEnd?: () => void
    profile?: string
}) => {
    const durationEndedRef = useRef(false)
    const [slot, setSlot] = useState<HTMLElement | null>(null)

    const isRecording = !!proctoring?.isRecording

    const initialRemainingSeconds = useMemo(() => {
        if (!referenceId) return durationSeconds
        return readProctoringTimerRemaining(referenceId, durationSeconds) ?? durationSeconds
    }, [referenceId, durationSeconds])

    useEffect(() => {
        const findSlot = () => document.getElementById(PROCTORING_HEADER_SLOT_ID)

        const existing = findSlot()
        if (existing) {
            setSlot(existing)
            return undefined
        }

        const observer = new MutationObserver(() => {
            const next = findSlot()
            if (next) {
                setSlot(next)
                observer.disconnect()
            }
        })
        observer.observe(document.body, { childList: true, subtree: true })
        return () => observer.disconnect()
    }, [])

    const handleComplete = useCallback(() => {
        if (durationEndedRef.current) return
        durationEndedRef.current = true
        onDurationEnd?.()
    }, [onDurationEnd])

    if (!proctoring || !slot) return null

    return createPortal(
        <div className={styles.chrome} data-testid="proctoring-top-chrome">
            {isRecording ? <ProctoringRecordingBadge profile={profile} /> : null}
            <ProctoringCountdownTimer
                durationSeconds={durationSeconds}
                initialRemainingSeconds={initialRemainingSeconds}
                storageReferenceId={referenceId}
                active
                onComplete={handleComplete}
            />
        </div>,
        slot
    )
}
